
import { defineComponent, ref, computed, getCurrentInstance, PropType, onMounted, onUnmounted } from 'vue';
import Toast from 'vant/lib/toast';
import UserInfoItem from '../user-info-item/index.vue';
import CaptionItem from '../caption-item/index.vue';
import PictureActionItem from '../picture-action-item/index.vue';
import { sendLog } from '~/common/radar';
import { getQuery } from '~/common/utils';
import { Picture } from '~/api/topic';

export default defineComponent({
    components: {
        CaptionItem,
        UserInfoItem,
        PictureActionItem,
    },
    props: {
        isLoginStatus: {
            type: Boolean,
            default: false,
        },
        index: {
            type: Number,
            default: 0,
        },
        picture: {
            type: Object as PropType<Picture>,
            default: () => ({}),
        },
        discoverCurrentIndex: {
            type: Number,
            default: 0,
        },
    },
    emits: ['onTargetPhotoFocused'],
    setup(props, { emit }) {
        const { proxy } = getCurrentInstance() as { proxy: any };

        const pictureCardRef = ref<HTMLElement>();
        const focusCurrentVideo = ref(false);
        let cardIntersectionObserver: IntersectionObserver;

        const jumpLink = computed(() => `/@${props.picture?.kwai_id}/picture/${props.picture?.pic_id_str}`);
        const pictureUrl = computed(() => props.picture?.pic_thumb_url || '');

        const showImageDom = computed(() => {
            const diff = props.index - props.discoverCurrentIndex;
            // index == 0时，只渲染第0个卡片
            // 不为0时，渲染 当前位置前2加后5卡片
            const isShow = (props.discoverCurrentIndex === 0 ? diff <= 0 : diff <= 5 && diff >= -2) || focusCurrentVideo.value;

            return isShow;
        });

        // IntersectionObserver 用于监听图片是否在视窗内
        onMounted(() => {
            const viewHeight = document.documentElement.clientHeight;

            cardIntersectionObserver = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        focusCurrentVideo.value = entry.isIntersecting;

                        if (
                            entry.isIntersecting &&
                            entry.boundingClientRect.top > 0 &&
                            entry.intersectionRatio > 0.8 &&
                            entry.boundingClientRect.bottom <= viewHeight
                        ) {
                            emit('onTargetPhotoFocused', props.index);
                        }
                    });
                },
                {
                    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
                }
            );

            pictureCardRef.value && cardIntersectionObserver.observe(pictureCardRef.value);
        });

        onUnmounted(() => {
            cardIntersectionObserver?.disconnect?.();
        });

        const cardContainerStyle = computed(() => {
            const { origin_width: width = 0, origin_height: height = 0 } = props.picture || {};

            if (width <= 0 || height <= 0) {
                return { width: '100vw', height: '100vw' };
            }

            let aspectRatio = (height / width) * 100;

            // 对第一个卡片的特殊处理，确保其高度最小为100vw，保证为最大元素，优化LCP性能
            if (props.index === 0) {
                aspectRatio = Math.max(aspectRatio, 100);
            }

            // 防止有些图片长宽比过大
            aspectRatio = Math.min(aspectRatio, 200);

            return { width: '100vw', height: `${aspectRatio}vw` };
        });

        const cardCommonParams = computed(() => ({
            card_type: 'photo_text',
            card_position: props.index,
        }));

        const caption = computed(() => {
            const caption = props.picture?.caption;
            return caption && caption.length > 5 ? caption : '';
        });

        const logClick = (buttonType: string) => {
            sendLog({
                type: 'click',
                name: 'PHOTO_CARD_SPECIAL',
                value: { button_type: buttonType, ...cardCommonParams.value },
            });
        };

        const onAvatarOrNameClick = () => {
            logClick('author_icon');

            Toast({ message: proxy.$root.$t('picture_click_user_toast') });
        };

        const handlePictureClick = () => {
            logClick('photo_text');

            const query = getQuery() || {};
            proxy?.$store?.commit('setClickedPicture', props.picture);
            proxy?.$router?.push({ path: jumpLink.value, query: { ...query, page_source: 'new_discover' } });
        };

        return {
            caption,
            pictureCardRef,
            cardContainerStyle,
            cardCommonParams,
            showImageDom,
            jumpLink,
            origin: window?.location?.origin ?? '',
            pictureUrl,

            onAvatarOrNameClick,
            handlePictureClick,
        };
    },
});
